import { useCallback, useEffect, useState } from 'react'
import type { ChangeEvent, Dispatch, SetStateAction } from 'react'
import type { Address } from 'src/components/checkout/steps/shipping/types'
import type { OrderForm } from 'src/sdk/checkout/entity/orderForm'
import { addressService } from 'src/services/addressService'
import { fetchCepData } from 'src/utils/fetchCepData'

import {
  buildGuestPostalOnlyAddress,
  formatGuestAddressPreview,
  isValidCepLookup,
  shouldSkipGuestAddressPersistence,
} from '../domain/guestPostalFlow'
import { isCompletePostalCode, maskPostalCodeTyping } from '../utils/postalCode'

const CEP_NOT_FOUND_MESSAGE = 'CEP não encontrado'

interface UseGuestPostalFlowParams {
  showModal: boolean
  inputValue: string
  setInputValue: Dispatch<SetStateAction<string>>
  setProvisionalAddress: (address: Address | null) => void
  orderForm: OrderForm
  setOrderForm: (orderForm: OrderForm) => void
  setPostalCode: (postalCode: string) => void
  onConfirmPostalCode: (value: string) => Promise<void>
  onContinueClose: (buttonName: string) => void
}

export function useGuestPostalFlow({
  showModal,
  inputValue,
  setInputValue,
  setProvisionalAddress,
  orderForm,
  setOrderForm,
  setPostalCode,
  onConfirmPostalCode,
  onContinueClose,
}: UseGuestPostalFlowParams) {
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [resolvedAddress, setResolvedAddress] = useState<Address | null>(null)
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)

  const clearFlowState = useCallback(() => {
    setInputValue('')
    setLookupError(null)
    setResolvedAddress(null)
    setProvisionalAddress(null)
  }, [setInputValue, setProvisionalAddress])

  const handlePostalCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = maskPostalCodeTyping(event.target.value)

      setInputValue(value)
      setLookupError(null)
      setResolvedAddress(null)
      setProvisionalAddress(null)
    },
    [setInputValue, setProvisionalAddress]
  )

  const persistPostalCodeAndClose = useCallback(
    async (buttonName: string) => {
      await onConfirmPostalCode(inputValue)
      setPostalCode(maskPostalCodeTyping(inputValue))
      onContinueClose(buttonName)
      clearFlowState()
    },
    [
      clearFlowState,
      inputValue,
      onConfirmPostalCode,
      onContinueClose,
      setPostalCode,
    ]
  )

  const handleApplyClick = useCallback(async () => {
    if (!isCompletePostalCode(inputValue) || isLookingUp) {
      return
    }

    setIsLookingUp(true)
    setLookupError(null)
    setResolvedAddress(null)

    try {
      const cepData = await fetchCepData(inputValue)

      if (!isValidCepLookup(cepData)) {
        setProvisionalAddress(null)
        setLookupError(CEP_NOT_FOUND_MESSAGE)

        return
      }

      const nextAddress = buildGuestPostalOnlyAddress(cepData, inputValue)

      if (shouldSkipGuestAddressPersistence(nextAddress)) {
        await persistPostalCodeAndClose('Aplicar')

        return
      }

      setProvisionalAddress(nextAddress)
      setResolvedAddress(nextAddress)
    } catch {
      setProvisionalAddress(null)
      setLookupError(CEP_NOT_FOUND_MESSAGE)
    } finally {
      setIsLookingUp(false)
    }
  }, [
    inputValue,
    isLookingUp,
    persistPostalCodeAndClose,
    setProvisionalAddress,
  ])

  const handleConfirmClick = useCallback(async () => {
    if (!resolvedAddress || isConfirming) {
      return
    }

    setIsConfirming(true)

    try {
      if (!shouldSkipGuestAddressPersistence(resolvedAddress)) {
        await addressService.createAddress({
          orderFormId: orderForm.orderFormId,
          address: {
            ...resolvedAddress,
            geoCoordinates: [],
          } as ShippingAddress,
          syncOrderFormFn: setOrderForm,
        })
      }

      await persistPostalCodeAndClose('Sim, utilizar endereço')
    } catch {
      console.error('Error confirming guest postal code')
    } finally {
      setIsConfirming(false)
    }
  }, [
    isConfirming,
    orderForm.orderFormId,
    persistPostalCodeAndClose,
    resolvedAddress,
    setOrderForm,
  ])

  const handleUseOtherAddress = useCallback(() => {
    if (isConfirming) {
      return
    }

    setLookupError(null)
    setResolvedAddress(null)
    setProvisionalAddress(null)
  }, [isConfirming, setProvisionalAddress])

  useEffect(() => {
    if (!showModal) {
      clearFlowState()
    }
  }, [clearFlowState, showModal])

  return {
    lookupError,
    isLookingUp,
    isConfirming,
    isApplyEnabled: isCompletePostalCode(inputValue) && !isLookingUp,
    addressPreview: resolvedAddress
      ? formatGuestAddressPreview(resolvedAddress)
      : '',
    handlePostalCodeChange,
    handleApplyClick,
    handleConfirmClick,
    handleUseOtherAddress,
  }
}
